/**
 * "Change role" modal for an existing Pool operator (30/7). Backend fact this whole flow leans on:
 * POST /pool/operators is an UPSERT (`ON CONFLICT ... DO UPDATE role`) — re-POSTing an email that
 * already holds a `pool.operator` row IS the change-role call, and the server's own audit distinguishes
 * a grant from a re-grant (see the `operator.grant` action, which this write shares with AddUserModal's
 * own POST). So this modal is deliberately a thin wrapper around the SAME `useAddPoolOperator` hook
 * AddUserModal already uses — no new endpoint, no new hook.
 *
 * Shows the operator's CURRENT role, lets the operator pick Admin/Viewer via the same `RoleCard`
 * (RoleCards.tsx) AddUserModal uses, and disables Save until the picked role actually differs from the
 * current one (there is nothing to submit otherwise — re-POSTing the SAME role would just be a no-op
 * write with its own audit row, which reads as noise). A 409/422 — including the server's own
 * self-change refusal, which PoolSettingsPage's own row-hiding already keeps an operator from reaching
 * in the normal click path — renders INLINE (role=alert), never a toast: the exact same reasoning as
 * every other Pool Settings dialog (a toast sinks behind this native <dialog>'s own top-layer backdrop
 * while it's still open).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiRequestError } from "@/api/client";
import { Button, Modal, StatusPill, useNotifier } from "@/components/ui";
import { useAddPoolOperator } from "@/features/pool/hooks";
import { RoleCard } from "@/features/pool/RoleCards";
import type { PoolOperatorRole, PoolOperatorRow } from "@/api/types";
import type { TKey } from "@/i18n/keys";

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

/** Duplicated locally rather than imported from PoolSettingsPage (not exported there either) — same
 *  convention as AddUserModal's own local PORTAL_ROLE_LABEL_KEY: a tiny 2-entry map used in exactly
 *  one place beats a shared import. */
const OPERATOR_ROLE_LABEL_KEY: Record<PoolOperatorRole, TKey> = {
  admin: "role.admin",
  viewer: "role.viewer",
};

export function ChangeRoleModal({
  operator,
  onClose,
}: {
  operator: PoolOperatorRow;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { notify } = useNotifier();
  const mut = useAddPoolOperator();

  const [role, setRole] = useState<PoolOperatorRole>(operator.role);
  const [error, setError] = useState<string | null>(null);

  const unchanged = role === operator.role;

  function submit() {
    if (unchanged) return;
    setError(null);
    mut.mutate(
      { email: operator.email, role },
      {
        onSuccess: () => {
          notify({
            tone: "ok",
            title: t("pool.settings.changeRoleSuccess", { email: operator.email }),
          });
          onClose();
        },
        onError: (e) => setError(errMessage(e, t("state.errorTitle"))),
      },
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t("pool.settings.changeRoleModalTitle", { email: operator.email })}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button loading={mut.isPending} disabled={unchanged} onClick={submit}>
            {t("pool.settings.changeRoleSubmitButton")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("pool.settings.changeRoleCurrentRoleLabel")}
          </span>
          <StatusPill tone={operator.role === "admin" ? "ok" : "neutral"}>
            {t(OPERATOR_ROLE_LABEL_KEY[operator.role])}
          </StatusPill>
        </div>

        <div
          role="radiogroup"
          aria-label={t("pool.settings.addUserRoleStepTitle")}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <RoleCard
            title={t("role.admin")}
            description={t("pool.settings.addUserRoleAdminDesc")}
            selected={role === "admin"}
            onSelect={() => setRole("admin")}
          />
          <RoleCard
            title={t("role.viewer")}
            description={t("pool.settings.addUserRoleViewerDesc")}
            selected={role === "viewer"}
            onSelect={() => setRole("viewer")}
          />
        </div>

        {unchanged && (
          <p className="text-(length:--text-xs) text-(--text-subtle)">
            {t("pool.settings.changeRoleUnchangedHint")}
          </p>
        )}

        {/* Rendered inline rather than as a toast — same reasoning as AddUserModal's own deviation. */}
        {error && (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
